import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MODE_CONFIG } from "./modeConfig";
import { verifyAccess } from "./api";

const ModeCtx = createContext(null);

function detectModeFromPath(pathname) {
  if (pathname.startsWith("/mba")) return "mba";
  if (pathname.startsWith("/member")) return "member";
  return "public";
}

export function ModeProvider({ children }) {
  const location = useLocation();
  const [mode, setMode] = useState("public");
  const [memberToken, setMemberToken] = useState(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams(location.search);
    const wm = params.get("wm");
    const pathMode = detectModeFromPath(location.pathname);

    async function resolve() {
      setResolving(true);
      // If wm token present, verify with backend → may upgrade to member.
      if (wm) {
        try {
          const res = await verifyAccess(wm);
          if (!active) return;
          if (res.valid && res.mode === "member") {
            setMode("member");
            setMemberToken(wm);
            try { localStorage.setItem("pisa_member_token", wm); } catch {}
            setResolving(false);
            return;
          }
        } catch (e) {
          // ignore — fall through
        }
      }
      // No valid wm in URL. Path mode wins for /mba and /member, otherwise public.
      if (pathMode === "member") {
        // /member route accessed without a valid token — try localStorage
        try {
          const stored = localStorage.getItem("pisa_member_token");
          if (stored) {
            const res = await verifyAccess(stored);
            if (active && res.valid && res.mode === "member") {
              setMode("member");
              setMemberToken(stored);
              setResolving(false);
              return;
            }
          }
        } catch {}
        // No valid token → fall back to public mode for safety
        if (active) {
          setMode("public");
          setMemberToken(null);
          setResolving(false);
          return;
        }
      }
      if (active) {
        setMode(pathMode);
        setMemberToken(null);
        setResolving(false);
      }
    }
    resolve();
    return () => { active = false; };
  }, [location.pathname, location.search]);

  const config = MODE_CONFIG[mode];

  const value = {
    mode,
    config,
    memberToken,
    resolving,
    setMode,
    setMemberToken,
  };

  return <ModeCtx.Provider value={value}>{children}</ModeCtx.Provider>;
}

export function useMode() {
  const ctx = useContext(ModeCtx);
  if (!ctx) throw new Error("useMode must be used within ModeProvider");
  return ctx;
}
